import { useState } from "react";
import { format } from "date-fns";
import { CalendarDays, Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { postAppointment, postFranchise } from "@/lib/api";
import { SERVICE_OPTIONS, TIME_SLOTS } from "@/data/content";

const fieldCls = "h-12 rounded-xl border-pine/15 bg-white focus-visible:ring-pine";
const selectCls = `${fieldCls} w-full px-3.5 text-sm font-medium cursor-pointer focus:outline-none focus:ring-2`;

function DateField({ value, onChange, testid }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`flex h-12 w-full items-center justify-between rounded-xl border border-pine/15 bg-white px-3.5 text-sm font-medium text-left ${value ? "text-pine-deep" : "text-slate-400"}`}
        >
          {value ? format(value, "PPP") : "Pick a preferred date"}
          <CalendarDays className="h-4 w-4 text-pine/50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={value} onSelect={onChange} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
      </PopoverContent>
    </Popover>
  );
}

function Success({ message, onReset, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col items-center text-center py-10 px-4">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-teal-soft">
        <CheckCircle2 className="h-8 w-8 text-teal-brand" />
      </span>
      <h3 className="mt-5 font-display text-2xl font-semibold text-pine-deep">Thank you!</h3>
      <p className="mt-2 max-w-sm text-sm text-slate-600">{message}</p>
      <button data-testid={`${testid}-reset`} onClick={onReset} className="mt-6 text-sm font-bold text-teal-brand hover:text-pine transition-colors">
        Submit another request
      </button>
    </div>
  );
}

export function AppointmentForm({ defaultService = "", onSuccess, testidPrefix = "appointment-form" }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", date: null, time: "", service: defaultService, message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error("Please pick a preferred date"); return; }
    if (!form.time) { toast.error("Please pick a preferred time"); return; }
    if (!form.service) { toast.error("Please choose a service"); return; }
    setBusy(true);
    try {
      const res = await postAppointment({
        name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim(),
        date: format(form.date, "PPP"), time: form.time, service: form.service,
        message: form.message.trim(), clinic: "Dyntra Clinics - Sector 75, Noida",
      });
      setDone(true);
      toast.success(res.message || "Thank you! Our clinic team will contact you shortly.");
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit right now. Please call us instead.");
    } finally {
      setBusy(false);
    }
  };

  if (done) return <Success testid={`${testidPrefix}-success`} message="Our clinic team will contact you shortly to confirm your appointment." onReset={() => { setDone(false); setForm({ name: "", phone: "", email: "", date: null, time: "", service: defaultService, message: "" }); }} />;

  return (
    <form data-testid={testidPrefix} onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
      <div>
        <Label htmlFor={`${testidPrefix}-name`}>Full Name</Label>
        <Input id={`${testidPrefix}-name`} data-testid={`${testidPrefix}-name-input`} required minLength={2} value={form.name} onChange={set("name")} placeholder="Your full name" className={fieldCls} />
      </div>
      <div>
        <Label htmlFor={`${testidPrefix}-phone`}>Mobile Number</Label>
        <Input id={`${testidPrefix}-phone`} data-testid={`${testidPrefix}-phone-input`} required pattern="[0-9+\-\s()]{8,16}" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={fieldCls} />
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor={`${testidPrefix}-email`}>Email</Label>
        <Input id={`${testidPrefix}-email`} data-testid={`${testidPrefix}-email-input`} type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={fieldCls} />
      </div>
      <div>
        <Label>Preferred Date</Label>
        <DateField value={form.date} onChange={(d) => setForm((f) => ({ ...f, date: d }))} testid={`${testidPrefix}-date-input`} />
      </div>
      <div>
        <Label>Preferred Time</Label>
        <select data-testid={`${testidPrefix}-time-select`} value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} className={selectCls}>
          <option value="" disabled>Pick a time slot</option>
          {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <Label>Service Required</Label>
        <select data-testid={`${testidPrefix}-service-select`} value={form.service} onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))} className={selectCls}>
          <option value="" disabled>Choose a service</option>
          {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor={`${testidPrefix}-message`}>Message <span className="text-slate-400 font-normal">(optional)</span></Label>
        <Textarea id={`${testidPrefix}-message`} data-testid={`${testidPrefix}-message-input`} rows={3} value={form.message} onChange={set("message")} placeholder="Anything we should know?" className="rounded-xl border-pine/15 bg-white focus-visible:ring-pine" />
      </div>
      <div className="sm:col-span-2">
        <button
          type="submit"
          data-testid={`${testidPrefix}-submit`}
          disabled={busy}
          className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-pine px-7 py-4 text-sm font-bold text-ivory shadow-lift transition-all duration-300 hover:bg-pine-deep disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
          {busy ? "Sending..." : "Request an Appointment"}
        </button>
      </div>
    </form>
  );
}

const BACKGROUNDS = ["Dentist / Doctor", "Healthcare Professional", "Entrepreneur / Investor", "Other Professional"];
const INVESTMENTS = ["Under Rs. 25 Lakh", "Rs. 25 - 50 Lakh", "Rs. 50 Lakh - 1 Crore", "Above Rs. 1 Crore", "Prefer to discuss"];

export function FranchiseForm({ testidPrefix = "franchise-form" }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", location: "", background: "", investment: "", existing_clinic: "No", source: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.background || !form.investment) { toast.error("Please complete all fields"); return; }
    setBusy(true);
    try {
      const res = await postFranchise({
        name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim(),
        city: form.city.trim(), location: form.location.trim(), background: form.background,
        investment: form.investment, existing_clinic: form.existing_clinic, source: form.source, message: form.message.trim(),
      });
      setDone(true);
      toast.success(res.message || "Thank you! Our franchise team will reach out with details.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit right now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) return <Success testid={`${testidPrefix}-success`} message="Our franchise team will contact you with detailed information about the Dyntra Clinics opportunity." onReset={() => { setDone(false); setForm({ name: "", phone: "", email: "", city: "", location: "", background: "", investment: "", existing_clinic: "No", source: "", message: "" }); }} />;

  return (
    <form data-testid={testidPrefix} onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
      <div>
        <Label htmlFor={`${testidPrefix}-name`}>Name</Label>
        <Input id={`${testidPrefix}-name`} data-testid={`${testidPrefix}-name-input`} required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={fieldCls} />
      </div>
      <div>
        <Label htmlFor={`${testidPrefix}-phone`}>Phone</Label>
        <Input id={`${testidPrefix}-phone`} data-testid={`${testidPrefix}-phone-input`} required pattern="[0-9+\-\s()]{8,16}" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={fieldCls} />
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor={`${testidPrefix}-email`}>Email</Label>
        <Input id={`${testidPrefix}-email`} data-testid={`${testidPrefix}-email-input`} type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" className={fieldCls} />
      </div>
      <div>
        <Label htmlFor={`${testidPrefix}-city`}>City</Label>
        <Input id={`${testidPrefix}-city`} data-testid={`${testidPrefix}-city-input`} required value={form.city} onChange={set("city")} placeholder="Your city" className={fieldCls} />
      </div>
      <div>
        <Label htmlFor={`${testidPrefix}-location`}>Preferred Location</Label>
        <Input id={`${testidPrefix}-location`} data-testid={`${testidPrefix}-location-input`} required value={form.location} onChange={set("location")} placeholder="Area / locality" className={fieldCls} />
      </div>
      <div>
        <Label>Professional Background</Label>
        <select data-testid={`${testidPrefix}-background-select`} value={form.background} onChange={(e) => setForm((f) => ({ ...f, background: e.target.value }))} className={selectCls}>
          <option value="" disabled>Select background</option>
          {BACKGROUNDS.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>
      <div>
        <Label>Investment Range</Label>
        <select data-testid={`${testidPrefix}-investment-select`} value={form.investment} onChange={(e) => setForm((f) => ({ ...f, investment: e.target.value }))} className={selectCls}>
          <option value="" disabled>Select range</option>
          {INVESTMENTS.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <Label>Do you already run a clinic?</Label>
        <div className="mt-2 flex gap-3">
          {["Yes", "No"].map((v) => (
            <button
              key={v}
              type="button"
              data-testid={`${testidPrefix}-existing-${v.toLowerCase()}`}
              onClick={() => setForm((f) => ({ ...f, existing_clinic: v }))}
              className={`rounded-full px-6 py-2.5 text-sm font-bold border transition-all duration-300 ${form.existing_clinic === v ? "bg-pine text-ivory border-pine" : "bg-white text-pine border-pine/20 hover:border-pine/50"}`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>
      <div className="sm:col-span-2">
        <Label>How did you hear about Dyntra? <span className="text-slate-400 font-normal">(optional)</span></Label>
        <select data-testid={`${testidPrefix}-source-select`} value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} className={selectCls}>
          <option value="">Select an option</option>
          {["Google Search", "Instagram / Social Media", "Friend or Referral", "Dyntra Store", "Other"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor={`${testidPrefix}-message`}>Message <span className="text-slate-400 font-normal">(optional)</span></Label>
        <Textarea id={`${testidPrefix}-message`} data-testid={`${testidPrefix}-message-input`} rows={3} value={form.message} onChange={set("message")} placeholder="Tell us about your plans" className="rounded-xl border-pine/15 bg-white focus-visible:ring-pine" />
      </div>
      <div className="sm:col-span-2">
        <button
          type="submit"
          data-testid={`${testidPrefix}-submit`}
          disabled={busy}
          className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-7 py-4 text-sm font-bold text-pine-deep shadow-lift transition-all duration-300 hover:bg-gold-soft disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
          {busy ? "Sending..." : "Request Franchise Information"}
        </button>
        <p className="mt-3 text-center text-xs text-slate-500">
          Your information will only be used to contact you regarding your franchise inquiry.
        </p>
      </div>
    </form>
  );
}
